import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { sendFriendInvitation, type FriendInvitation } from '@/services/invitationService';
import { CheckCircle, Clock, Mail } from 'lucide-react';

interface InviteFriendsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    playerId: string;
    invitations: FriendInvitation[];
    remaining: number;
    onInvitationSent: () => void;
}

export function InviteFriendsModal({
    open,
    onOpenChange,
    playerId,
    invitations,
    remaining,
    onInvitationSent
}: InviteFriendsModalProps) {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email.trim()) {
            toast.error('Please enter an email address');
            return;
        }

        setIsLoading(true);
        const result = await sendFriendInvitation(playerId, email);
        setIsLoading(false);

        if (result.success) {
            toast.success('Invitation sent successfully!');
            setEmail('');
            onInvitationSent();
        } else {
            toast.error(result.error || 'Failed to send invitation');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Invite Friends</DialogTitle>
                    <DialogDescription>
                        Invite up to 5 friends and earn 20 points for each friend who registers!
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y- 4 py-4">
                    {/* Progress */}
                    <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                            <span className="font-medium">Validated Invitations</span>
                            <span className="text-muted-foreground">
                                {invitations.filter(inv => inv.is_registered).length}/5
                            </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                            <div
                                className="bg-primary h-2 rounded-full transition-all"
                                style={{ width: `${(invitations.filter(inv => inv.is_registered).length / 5) * 100}%` }}
                            />
                        </div>
                    </div>

                    {/* Invitation Form */}
                    {remaining > 0 ? (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Friend's Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="friend@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isLoading}
                                />
                                <p className="text-xs text-muted-foreground">
                                    {remaining} invitation{remaining !== 1 ? 's' : ''} remaining
                                </p>
                            </div>
                            <Button type="submit" disabled={isLoading} className="w-full">
                                <Mail className="w-4 h-4 mr-2" />
                                {isLoading ? 'Sending...' : 'Send Invitation'}
                            </Button>
                        </form>
                    ) : (
                        <div className="text-center text-sm text-muted-foreground py-4">
                            You've reached the maximum number of invitations (5/5)
                        </div>
                    )}

                    {/* Invitations List */}
                    {invitations.length > 0 && (
                        <div className="space-y-2 mt-6">
                            <h4 className="font-medium text-sm">Your Invitations</h4>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                {invitations.map((invitation) => (
                                    <div
                                        key={invitation.id}
                                        className="flex items-center justify-between p-3 border rounded-lg"
                                    >
                                        <div className="flex items-center gap-3">
                                            {invitation.is_registered ? (
                                                <CheckCircle className="w-4 h-4 text-green-500" />
                                            ) : (
                                                <Clock className="w-4 h-4 text-muted-foreground" />
                                            )}
                                            <span className="text-sm">{invitation.invited_email}</span>
                                        </div>
                                        <Badge variant={invitation.is_registered ? "default" : "outline"} className="text-xs">
                                            {invitation.is_registered ? 'Registered' : 'Pending'}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
